import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
    ActionIcon, Box, Textarea, Paper, ScrollArea, Text,
    Tooltip, Badge, Transition, Loader, Group, CloseButton,
    Stack, ThemeIcon,
} from '@mantine/core';
import { useHotkeys } from '@mantine/hooks';
import {
    IconMessageChatbot, IconSend, IconTrash, IconArrowDown,
    IconBolt, IconCircleFilled, IconRefresh,
} from '@tabler/icons-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
    sendMessageStream, clearChat, checkConnection, getChatbotUrl,
    type ChatMessage, type StreamCallbacks, type ConnectionStatus, type DonePayload,
} from '../../services/ChatBotService';
import { processMarkers, hasMarkers, hasHtmlContent, toggleProfileCard, installGlobalHandlers } from './processMarkers';

installGlobalHandlers();

const MAX_MESSAGES = 200;

const STATUS_CONFIG: Record<ConnectionStatus, { color: string; label: string }> = {
    checking: { color: 'yellow', label: 'Connecting...' },
    online: { color: 'green', label: 'Online' },
    offline: { color: 'red', label: 'Offline' },
    'auth-failed': { color: 'orange', label: 'Auth Error' },
};

export default function ChatWidget() {
    const [open, setOpen] = useState(false);
    const [messages, setMessages] = useState<ChatMessage[]>([]);
    const [input, setInput] = useState('');
    const [loading, setLoading] = useState(false);
    const [statusText, setStatusText] = useState('');
    const [atBottom, setAtBottom] = useState(true);
    const [connection, setConnection] = useState<ConnectionStatus>('checking');

    const viewportRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLTextAreaElement>(null);
    const abortRef = useRef<AbortController | null>(null);
    const hasChecked = useRef(false);

    useHotkeys([['mod+shift+k', () => setOpen(o => !o)]]);

    const runHealthCheck = useCallback(async () => {
        setConnection('checking');
        const status = await checkConnection();
        setConnection(status);
        if (status === 'offline') {
            console.warn(`[Hades AI] Backend unreachable at ${getChatbotUrl()}`);
        }
    }, []);

    useEffect(() => {
        if (open && !hasChecked.current) {
            hasChecked.current = true;
            runHealthCheck();
        }
    }, [open, runHealthCheck]);

    const scrollToBottom = useCallback(() => {
        requestAnimationFrame(() => {
            viewportRef.current?.scrollTo({ top: viewportRef.current.scrollHeight, behavior: 'smooth' });
        });
    }, []);

    useEffect(() => {
        if (open && messages.length > 0) scrollToBottom();
    }, [open, messages.length, scrollToBottom]);

    useEffect(() => {
        if (open) inputRef.current?.focus();
    }, [open]);

    const handleScroll = useCallback(() => {
        const el = viewportRef.current;
        if (!el) return;
        setAtBottom(el.scrollHeight - el.scrollTop - el.clientHeight < 60);
    }, []);

    const addMessage = useCallback((msg: ChatMessage) => {
        setMessages(prev => {
            const next = [...prev, msg];
            return next.length > MAX_MESSAGES ? next.slice(-MAX_MESSAGES) : next;
        });
    }, []);

    const handleSend = useCallback(async () => {
        const trimmed = input.trim();
        if (!trimmed || loading) return;

        if (connection === 'offline') {
            addMessage({
                role: 'system',
                content: `Cannot reach the assistant at ${getChatbotUrl()}. Check that the backend is running and REACT_APP_CHATBOT_URL is set correctly.`,
                timestamp: new Date(),
            });
            return;
        }

        const userMsg: ChatMessage = { role: 'user', content: trimmed, timestamp: new Date() };
        addMessage(userMsg);
        setInput('');
        setLoading(true);
        setStatusText('Thinking...');

        const controller = new AbortController();
        abortRef.current = controller;
        const timeout = setTimeout(() => controller.abort(), 120_000);

        const streamingMsg: ChatMessage = {
            role: 'assistant', content: '', timestamp: new Date(), isStreaming: true,
        };
        setMessages(prev => [...prev, streamingMsg]);

        const callbacks: StreamCallbacks = {
            onContent: (text) => {
                setMessages(prev => {
                    const copy = [...prev];
                    const last = copy[copy.length - 1];
                    if (last?.isStreaming) {
                        copy[copy.length - 1] = { ...last, content: text };
                    }
                    return copy;
                });
                setStatusText('');
                scrollToBottom();
            },
            onFunctionCall: (name) => {
                setStatusText(`Calling ${name}...`);
            },
            onDone: (payload) => {
                setMessages(prev => {
                    const copy = [...prev];
                    const last = copy[copy.length - 1];
                    if (last?.isStreaming) {
                        copy[copy.length - 1] = {
                            ...last,
                            content: payload.response,
                            isStreaming: false,
                            profileCards: payload.profileCards,
                            htmlTables: payload.htmlTables,
                        };
                    }
                    return copy;
                });
                setLoading(false);
                setStatusText('');
                scrollToBottom();
            },
            onError: (error) => {
                setMessages(prev => {
                    const copy = prev.filter(m => !m.isStreaming);
                    return [...copy, { role: 'system', content: error, timestamp: new Date() }];
                });
                setLoading(false);
                setStatusText('');
                if (error.toLowerCase().includes('failed to fetch') || error.toLowerCase().includes('networkerror')) {
                    setConnection('offline');
                }
            },
        };

        try {
            await sendMessageStream(trimmed, callbacks, controller.signal);
        } catch {
            if (!controller.signal.aborted) {
                callbacks.onError(`Failed to connect to assistant at ${getChatbotUrl()}`);
                setConnection('offline');
            }
        } finally {
            clearTimeout(timeout);
            abortRef.current = null;
        }
    }, [input, loading, connection, addMessage, scrollToBottom]);

    const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    }, [handleSend]);

    const handleClear = useCallback(async () => {
        if (loading) {
            abortRef.current?.abort();
            setLoading(false);
        }
        setMessages([]);
        setStatusText('');
        try { await clearChat(); } catch { /* ignore */ }
    }, [loading]);

    const statusCfg = STATUS_CONFIG[connection];

    return (
        <>
            {/* Footer toggle button */}
            {!open && (
                <Tooltip label="Ask Hades AI (Ctrl+Shift+K)" position="top" withArrow>
                    <ActionIcon
                        size="lg"
                        radius="md"
                        variant="filled"
                        color="brand"
                        onClick={() => setOpen(true)}
                        aria-label="Open chat assistant"
                    >
                        <IconMessageChatbot size={20} />
                    </ActionIcon>
                </Tooltip>
            )}

            {/* Chat Panel */}
            <Transition mounted={open} transition="slide-up" duration={250}>
                {(styles) => (
                    <Paper
                        shadow="xl"
                        radius="lg"
                        style={{
                            ...styles,
                            position: 'fixed',
                            bottom: 48,
                            right: 24,
                            width: 460,
                            height: 600,
                            maxHeight: 'calc(100vh - 100px)',
                            zIndex: 1000,
                            display: 'flex',
                            flexDirection: 'column',
                            overflow: 'hidden',
                            border: '1px solid var(--mantine-color-dark-4)',
                        }}
                    >
                        {/* Header */}
                        <Group
                            justify="space-between"
                            px="md"
                            py="xs"
                            style={{
                                borderBottom: '1px solid var(--mantine-color-dark-4)',
                                background: 'var(--mantine-color-brand-9)',
                                borderRadius: 'var(--mantine-radius-lg) var(--mantine-radius-lg) 0 0',
                                flexShrink: 0,
                            }}
                        >
                            <Group gap="xs">
                                <ThemeIcon size="sm" variant="transparent" color="white">
                                    <IconBolt size={16} />
                                </ThemeIcon>
                                <Text fw={600} size="sm" c="white">Hades AI</Text>
                                <Tooltip label={`${statusCfg.label} — ${getChatbotUrl()}`}>
                                    <Group gap={4} style={{ cursor: 'default' }}>
                                        {connection === 'checking' ? (
                                            <Loader size={10} color="white" />
                                        ) : (
                                            <IconCircleFilled
                                                size={10}
                                                style={{ color: `var(--mantine-color-${statusCfg.color}-5)` }}
                                            />
                                        )}
                                        <Text size="xs" c="white" opacity={0.8}>
                                            {statusCfg.label}
                                        </Text>
                                    </Group>
                                </Tooltip>
                                {loading && <Loader size={14} color="white" type="dots" />}
                            </Group>
                            <Group gap={4}>
                                {connection === 'offline' && (
                                    <Tooltip label="Retry connection">
                                        <ActionIcon
                                            variant="subtle"
                                            color="white"
                                            size="sm"
                                            onClick={runHealthCheck}
                                        >
                                            <IconRefresh size={14} />
                                        </ActionIcon>
                                    </Tooltip>
                                )}
                                <Tooltip label="Clear conversation">
                                    <ActionIcon
                                        variant="subtle"
                                        color="white"
                                        size="sm"
                                        onClick={handleClear}
                                    >
                                        <IconTrash size={14} />
                                    </ActionIcon>
                                </Tooltip>
                                <CloseButton
                                    variant="subtle"
                                    c="white"
                                    size="sm"
                                    onClick={() => setOpen(false)}
                                />
                            </Group>
                        </Group>

                        {/* Messages */}
                        <ScrollArea
                            flex={1}
                            px="md"
                            py="sm"
                            viewportRef={viewportRef}
                            onScrollPositionChange={handleScroll}
                            style={{ minHeight: 0 }}
                        >
                            {messages.length === 0 && (
                                <Stack align="center" justify="center" gap="xs" py="xl">
                                    <IconMessageChatbot
                                        size={48}
                                        style={{ opacity: 0.3, color: 'var(--mantine-color-brand-4)' }}
                                    />
                                    <Text size="sm" c="dimmed" ta="center">
                                        Hi! I'm your Hades AI assistant.<br />
                                        Ask me about employees, students, schools, or anything DCPS.
                                    </Text>
                                    {connection === 'offline' && (
                                        <Text size="xs" c="red" ta="center" mt="xs">
                                            Cannot reach backend at {getChatbotUrl()}
                                        </Text>
                                    )}
                                </Stack>
                            )}
                            {messages.map((msg, i) => (
                                <MessageBubble key={i} message={msg} />
                            ))}
                            {statusText && (
                                <Text size="xs" c="dimmed" fs="italic" mt="xs">
                                    {statusText}
                                </Text>
                            )}
                        </ScrollArea>

                        {/* Scroll-to-bottom indicator */}
                        {!atBottom && messages.length > 3 && (
                            <ActionIcon
                                style={{
                                    position: 'absolute',
                                    bottom: 80,
                                    left: '50%',
                                    transform: 'translateX(-50%)',
                                    zIndex: 2,
                                }}
                                variant="filled"
                                color="dark"
                                radius="xl"
                                size="sm"
                                onClick={scrollToBottom}
                            >
                                <IconArrowDown size={14} />
                            </ActionIcon>
                        )}

                        {/* Input */}
                        <Box
                            px="md"
                            pb="md"
                            pt="xs"
                            style={{
                                borderTop: '1px solid var(--mantine-color-dark-4)',
                                flexShrink: 0,
                            }}
                        >
                            <Group gap="xs" align="flex-end">
                                <Textarea
                                    ref={inputRef}
                                    placeholder="Ask Hades anything..."
                                    value={input}
                                    onChange={(e) => setInput(e.currentTarget.value)}
                                    onKeyDown={handleKeyDown}
                                    autosize
                                    minRows={1}
                                    maxRows={4}
                                    disabled={loading}
                                    flex={1}
                                    styles={{
                                        input: {
                                            fontSize: 'var(--mantine-font-size-sm)',
                                            borderRadius: 'var(--mantine-radius-md)',
                                        },
                                    }}
                                />
                                <ActionIcon
                                    variant="filled"
                                    color="brand"
                                    size="lg"
                                    radius="md"
                                    onClick={handleSend}
                                    disabled={!input.trim() || loading}
                                    aria-label="Send message"
                                >
                                    <IconSend size={18} />
                                </ActionIcon>
                            </Group>
                        </Box>
                    </Paper>
                )}
            </Transition>
        </>
    );
}

/* ─── Message bubble ─── */

function MessageBubble({ message }: { message: ChatMessage }) {
    const isUser = message.role === 'user';
    const isSystem = message.role === 'system';

    const renderedHtml = React.useMemo(() => {
        if (isUser || isSystem || !message.content || message.isStreaming) return null;
        if (hasMarkers(message.content) || hasHtmlContent(message.content)) {
            return processMarkers(message.content, message.profileCards, message.htmlTables);
        }
        return null;
    }, [isUser, isSystem, message.content, message.isStreaming, message.profileCards, message.htmlTables]);

    const htmlRef = React.useCallback((node: HTMLDivElement | null) => {
        if (!node) return;
        node.querySelectorAll('[onclick]').forEach(el => el.removeAttribute('onclick'));
        node.addEventListener('click', (e: MouseEvent) => {
            const target = e.target as HTMLElement;
            const clickable = target.closest('.profile-card-summary, .profile-card-expand-btn');
            if (clickable) {
                e.stopPropagation();
                toggleProfileCard(clickable as HTMLElement);
            }
        });
    }, []);

    return (
        <Box
            mb="sm"
            style={{
                display: 'flex',
                justifyContent: isUser ? 'flex-end' : 'flex-start',
            }}
        >
            <Paper
                px="sm"
                py="xs"
                radius="md"
                style={{
                    maxWidth: '88%',
                    background: isUser
                        ? 'var(--mantine-color-brand-8)'
                        : isSystem
                            ? 'var(--mantine-color-red-9)'
                            : 'var(--mantine-color-dark-6)',
                    border: isUser ? 'none' : '1px solid var(--mantine-color-dark-5)',
                }}
            >
                {isUser || isSystem ? (
                    <Text size="sm" c="white" style={{ whiteSpace: 'pre-wrap' }}>
                        {message.content}
                    </Text>
                ) : (
                    <Box
                        className="chat-markdown"
                        fz="sm"
                        lh={1.55}
                    >
                        {message.content ? (
                            renderedHtml ? (
                                <div ref={htmlRef} dangerouslySetInnerHTML={{ __html: renderedHtml }} />
                            ) : (
                                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                    {message.content}
                                </ReactMarkdown>
                            )
                        ) : (
                            message.isStreaming && <Loader size={16} type="dots" />
                        )}
                    </Box>
                )}
                {message.isStreaming && message.content && (
                    <Badge size="xs" variant="dot" color="green" mt={4}>
                        streaming
                    </Badge>
                )}
            </Paper>
        </Box>
    );
}
